import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { StickyNote, Plus, Trash2, Edit, Search, Tag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notes = ({ user, onLogout }) => {
  const [notes, setNotes] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  
  // Form state - منفصل تماماً
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");
  const [caseId, setCaseId] = useState("none");
  const [tags, setTags] = useState("");

  useEffect(() => {
    fetchNotes();
    fetchCases();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes`);
      setNotes(response.data);
    } catch (error) {
      toast.error("خطأ في تحميل الملاحظات");
    } finally {
      setLoading(false);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      setCases(response.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title,
        content,
        category,
        case_id: caseId === "none" ? null : caseId,
        tags: tags ? tags.split(",").map(t => t.trim()) : []
      };

      if (editingNote) {
        await axios.put(`${API}/notes/${editingNote.id}`, payload);
        toast.success("تم تحديث الملاحظة بنجاح");
      } else {
        await axios.post(`${API}/notes`, payload);
        toast.success("تم إضافة الملاحظة بنجاح");
      }

      closeDialog();
      fetchNotes();
    } catch (error) {
      toast.error(editingNote ? "خطأ في تحديث الملاحظة" : "خطأ في إضافة الملاحظة");
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setTitle(note.title);
    setContent(note.content);
    setCategory(note.category);
    setCaseId(note.case_id || "none");
    setTags(note.tags ? note.tags.join(", ") : "");
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الملاحظة؟")) return;

    try {
      await axios.delete(`${API}/notes/${id}`);
      toast.success("تم حذف الملاحظة");
      fetchNotes();
    } catch (error) {
      toast.error("خطأ في حذف الملاحظة");
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingNote(null);
    // Reset form
    setTitle("");
    setContent("");
    setCategory("general");
    setCaseId("none");
    setTags("");
  };

  const getCategoryLabel = (cat) => {
    const categories = {
      general: "عام",
      legal_research: "بحث قانوني",
      case_strategy: "استراتيجية قضية",
      client_info: "معلومات عميل",
      court_notes: "ملاحظات محكمة",
      other: "أخرى"
    };
    return categories[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      general: "badge-blue",
      legal_research: "badge-green",
      case_strategy: "badge-yellow",
      client_info: "badge-red",
      court_notes: "bg-purple-100 text-purple-800",
      other: "bg-gray-100 text-gray-800"
    };
    return colors[cat] || "badge-blue";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const filteredNotes = notes.filter((note) => {
    const matchesSearch = 
      note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === "all" || note.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">الملاحظات</h1>
            <p className="text-lg text-slate-600">سجل أفكارك وملاحظاتك القانونية</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-note-button">
                <Plus className="w-5 h-5" />
                ملاحظة جديدة
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingNote ? "تعديل ملاحظة" : "إضافة ملاحظة جديدة"}</DialogTitle>
                <DialogDescription>
                  {editingNote ? "قم بتعديل تفاصيل الملاحظة" : "أضف ملاحظة جديدة لتنظيم أفكارك ومعلوماتك"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="note-title">عنوان الملاحظة *</Label>
                  <Input
                    id="note-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    placeholder="مثال: بحث عن المادة 123"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note-category">التصنيف *</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger id="note-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">عام</SelectItem>
                      <SelectItem value="legal_research">بحث قانوني</SelectItem>
                      <SelectItem value="case_strategy">استراتيجية قضية</SelectItem>
                      <SelectItem value="client_info">معلومات عميل</SelectItem>
                      <SelectItem value="court_notes">ملاحظات محكمة</SelectItem>
                      <SelectItem value="other">أخرى</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note-case">ربط بقضية (اختياري)</Label>
                  <Select value={caseId} onValueChange={setCaseId}>
                    <SelectTrigger id="note-case">
                      <SelectValue placeholder="اختر قضية" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">لا توجد</SelectItem>
                      {cases.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note-content">المحتوى *</Label>
                  <Textarea
                    id="note-content"
                    rows={8}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                    placeholder="اكتب ملاحظاتك هنا..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note-tags">وسوم (افصل بفاصلة)</Label>
                  <Input
                    id="note-tags"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="مثال: مهم, عاجل, للمراجعة"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    إلغاء
                  </Button>
                  <Button type="submit">
                    {editingNote ? "تحديث" : "حفظ"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="بحث في الملاحظات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger>
              <SelectValue placeholder="تصفية حسب التصنيف" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع التصنيفات</SelectItem>
              <SelectItem value="general">عام</SelectItem>
              <SelectItem value="legal_research">بحث قانوني</SelectItem>
              <SelectItem value="case_strategy">استراتيجية قضية</SelectItem>
              <SelectItem value="client_info">معلومات عميل</SelectItem>
              <SelectItem value="court_notes">ملاحظات محكمة</SelectItem>
              <SelectItem value="other">أخرى</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Notes Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-24 bg-slate-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredNotes.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <StickyNote className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">
                {searchTerm || filterCategory !== "all" 
                  ? "لا توجد ملاحظات مطابقة للبحث"
                  : "لا توجد ملاحظات حالياً. ابدأ بإضافة ملاحظة!"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNotes.map((note) => (
              <Card
                key={note.id}
                className="hover:shadow-lg transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl flex-1">{note.title}</CardTitle>
                    <span className={`badge ${getCategoryColor(note.category)} text-xs`}>
                      {getCategoryLabel(note.category)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">{formatDate(note.created_at)}</div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-4">
                    {note.content}
                  </p>
                  
                  {note.tags && note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {note.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded"
                        >
                          <Tag className="w-3 h-3" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(note)}
                      className="gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      تعديل
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(note.id)}
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      حذف
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notes;
